package org.example.project.screens_sys

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import org.example.project.cleaning.SharedWorkspaceStyle
import org.example.project.dashboard.DashboardFeedRegistry
import org.example.project.dashboard.DashboardNode
import org.example.project.service.FeedControlApi
import org.example.project.service.GlobalManager
import org.example.project.service.LocalScreenInternalTitleVisible
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaCheckbox
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.utils.All
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flex
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.maxWidth
import org.jetbrains.compose.web.css.minHeight
import org.jetbrains.compose.web.css.minWidth
import org.jetbrains.compose.web.css.overflowY
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text

private val FEED_CONTROL_NODES = listOf(
    All(key = DashboardNode.SYS, label = "SYS"),
    All(key = DashboardNode.CUS, label = "CUS"),
    All(key = DashboardNode.NET, label = "NET")
)

@Composable
fun SysFeedControlScreen() {
    Style(cssRules = SharedWorkspaceStyle.cssRules)

    val scope = rememberCoroutineScope()
    val internalTitleVisible = LocalScreenInternalTitleVisible.current
    var config by remember { mutableStateOf(DashboardFeedRegistry.defaultConfig()) }
    val feedConfigVersion = FeedControlApi.version()

    fun setFeedEnabled(feedKey: String, node: String, enabled: Boolean) {
        val current = config[feedKey].orEmpty()
        config = config + (feedKey to (current + (node to enabled.toString())))
    }

    suspend fun reloadConfig() {
        config = FeedControlApi.loadFeedConfig()
    }

    LaunchedEffect(feedConfigVersion) {
        reloadConfig()
    }

    Div({ classes(SharedWorkspaceStyle.workspace) }) {
        Div({ classes(SharedWorkspaceStyle.side, SharedWorkspaceStyle.content); style { width(100.percent) } }) {
            Div({
                classes(SharedWorkspaceStyle.contentInner)
                style {
                    maxWidth(100.percent)
                    flex(1)
                    minHeight(0.px)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                }
            }) {
                if (internalTitleVisible) {
                    Div({
                        classes(SharedWorkspaceStyle.rightTitle)
                        style { justifyContent(JustifyContent.Center) }
                    }) {
                        Span({ classes(SharedWorkspaceStyle.sectionTitle) }) { Text("Feed Control") }
                    }
                }

                Div({
                    classes(SharedWorkspaceStyle.actionBar)
                    style {
                        maxWidth(100.percent)
                        justifyContent(JustifyContent.Center)
                        property("grid-template-columns", "repeat(2, minmax(0, 160px))")
                    }
                }) {
                    LumaticaButton(
                        label = "Speichern",
                        onClick = {
                            scope.launch {
                                config = FeedControlApi.saveFeedConfig(config)
                                GlobalManager.triggerSessionConfigReload()
                            }
                        },
                        variant = LumaticaButtonVariant.Action,
                        heightPx = 42
                    )
                    LumaticaButton(
                        label = "Refresh",
                        onClick = { scope.launch { reloadConfig() } },
                        variant = LumaticaButtonVariant.Standard,
                        heightPx = 42
                    )
                }

                Div({ classes(SharedWorkspaceStyle.centeredDivider) })

                Div({
                    style {
                        flex(1)
                        minHeight(0.px)
                        overflowY("auto")
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(14.px)
                    }
                }) {
                    Div({
                        classes(SharedWorkspaceStyle.permissionHeader)
                        style { property("grid-template-columns", "minmax(260px, 1fr) 110px 110px 110px") }
                    }) {
                        Span({ classes(SharedWorkspaceStyle.permissionName) }) { Text("Feed") }
                        FEED_CONTROL_NODES.forEach { node ->
                            Span { Text(node.label) }
                        }
                    }

                    DashboardFeedRegistry.definitions.forEach { feed ->
                        Div({
                            classes(SharedWorkspaceStyle.permissionRow)
                            style {
                                property("grid-template-columns", "minmax(260px, 1fr) 110px 110px 110px")
                                alignItems(AlignItems.Center)
                            }
                        }) {
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    gap(12.px)
                                    minWidth(0.px)
                                    padding(4.px)
                                }
                            }) {
                                Img(feed.icon) {
                                    style { width(22.px); height(22.px) }
                                }
                                Div({
                                    style {
                                        display(DisplayStyle.Flex)
                                        flexDirection(FlexDirection.Column)
                                        gap(2.px)
                                        minWidth(0.px)
                                    }
                                }) {
                                    Span({ classes(SharedWorkspaceStyle.permissionName) }) { Text(feed.label) }
                                    Span({
                                        style { property("font-size", "12px"); property("opacity", "0.7") }
                                    }) { Text(feed.targetKey) }
                                }
                            }

                            FEED_CONTROL_NODES.forEach { node ->
                                val checked = FeedControlApi.isFeedEnabled(config, feed.key, node.key)
                                Div({
                                    style {
                                        display(DisplayStyle.Flex)
                                        alignItems(AlignItems.Center)
                                        justifyContent(JustifyContent.Center)
                                    }
                                }) {
                                    LumaticaCheckbox(
                                        checked = checked,
                                        onChange = { setFeedEnabled(feed.key, node.key, !checked) }
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
